/**
 * Document API Service
 *
 * API calls for document/invoice listing and management
 */

import { apiService, ApiResponse } from './api';

// =============================================================================
// Types
// =============================================================================

export interface DocumentItem {
    id: string;
    company_id: string;
    user_id: string;
    type: 'invoice' | 'quote';
    filename: string;
    file_size: number;
    ocr_status: 'pending' | 'processing' | 'completed' | 'failed';
    extraction_status?: 'pending' | 'completed' | 'failed';
    extraction_error?: string;
    extracted_text?: string;
    extracted_data?: {
        line_items?: any[];
        total_amount?: number;
        invoice_number?: string;
        invoice_date?: string;
        supplier_name?: string;
        vendor?: {
            name?: string;
            [key: string]: any;
        };
        financials?: {
            total_amount?: number;
            currency?: string;
            tax?: number;
            subtotal?: number;
        };
        [key: string]: any;
    };
    comparison?: {
        linked_quote_id?: string;
        status?: string;
        match_score?: number;
    };
    ocr_error?: string;
    created_at: string;
    updated_at: string;
}

export interface DocumentListResponse {
    documents: DocumentItem[];
    total: number;
    page: number;
    page_size: number;
    has_next: boolean;
    has_prev: boolean;
}

// =============================================================================
// API Functions
// =============================================================================

/**
 * Get list of documents/invoices
 */
export const getDocuments = async (
    page: number = 1,
    pageSize: number = 20,
    type?: 'invoice' | 'quote'
): Promise<ApiResponse<DocumentListResponse>> => {
    const params: Record<string, any> = { page, page_size: pageSize };
    if (type) params.type = type;
    return apiService.get<DocumentListResponse>('/api/documents', params);
};

/**
 * Get document by ID
 */
export const getDocument = async (
    documentId: string
): Promise<ApiResponse<DocumentItem>> => {
    return apiService.get<DocumentItem>(`/api/documents/${documentId}`);
};

/**
 * Delete document
 */
export const deleteDocument = async (
    documentId: string
): Promise<ApiResponse<void>> => {
    return apiService.delete<void>(`/api/documents/${documentId}`);
};

/**
 * Update extracted document fields
 */
export const updateDocumentFields = async (
    documentId: string,
    fields: Record<string, any>
): Promise<ApiResponse<void>> => {
    return apiService.patch<void>(
        `/api/documents/${documentId}/fields`,
        { fields }
    );
};

/**
 * Trigger reprocessing of pending/failed documents
 */
export const reprocessPending = async (): Promise<ApiResponse<{ reprocessed: number }>> => {
    return apiService.post<{ reprocessed: number }>('/api/documents/reprocess-pending');
};

// Export as object
const documentApi = {
    getDocuments,
    getDocument,
    deleteDocument,
    updateDocumentFields,
    reprocessPending
};

export default documentApi;
